// src/components/CreditTracker.jsx
// Tracks all credit sales, outstanding dues, and payment collections

import { useState } from "react";
import { OILS } from "../constants";
import { fmt, fmtL, today, OilDot, S, AlertBar, EmptyState } from "../utils/ui";

export function CreditTracker({ state, actions, user }) {
  const [tab, setTab] = useState("outstanding");
  const [showCollectForm, setShowCollectForm] = useState(null);
  const [collectAmount, setCollectAmount] = useState("");
  const [collectMode, setCollectMode] = useState("Cash");
  const [collectDate, setCollectDate] = useState(today());
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  // Get all credit sales
  const creditSales = state.sales.filter(s => s.mode === "Credit");
  const collections = state.collections || [];

  // Calculate outstanding per sale
  const salesWithBalance = creditSales.map(sale => {
    const paid = collections
      .filter(c => c.saleTs === sale.ts)
      .reduce((a, c) => a + Number(c.amount), 0);
    const balance = Number(sale.amount) - paid;
    return { ...sale, paid, balance };
  });

  const outstanding = salesWithBalance.filter(s => s.balance > 0);
  const cleared     = salesWithBalance.filter(s => s.balance <= 0);

  const totalOutstanding = outstanding.reduce((a, s) => a + s.balance, 0);
  const totalCredit      = creditSales.reduce((a, s) => a + Number(s.amount), 0);
  const totalCollected   = collections.reduce((a, c) => a + Number(c.amount), 0);

  const handleCollect = async () => {
    if (!collectAmount || Number(collectAmount) <= 0) return;
    setSaving(true);
    await actions.addCollection({
      saleTs:   showCollectForm.ts,
      amount:   Number(collectAmount),
      mode:     collectMode,
      date:     collectDate,
      oil:      showCollectForm.oil,
      location: showCollectForm.location,
      by:       user.name,
    }, user);
    setShowCollectForm(null);
    setCollectAmount("");
    setMsg("Payment recorded!"); setTimeout(() => setMsg(""), 2500);
    setSaving(false);
  };

  return (
    <div style={{ padding: "12px 14px" }}>
      {/* Collection modal */}
      {showCollectForm && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
          zIndex: 300, display: "flex", alignItems: "flex-end", justifyContent: "center",
        }}>
          <div style={{
            background: "#fff", borderRadius: "16px 16px 0 0",
            padding: "20px 16px 32px", width: "100%", maxWidth: 430,
          }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Record Payment</div>
            <div style={{ fontSize: 12, color: "#888", marginBottom: 16 }}>
              {showCollectForm.oil} · {showCollectForm.location} · {showCollectForm.date}
            </div>
            <div style={{ background: "#F7F4EE", borderRadius: 8, padding: "10px 12px", marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}>
                <span style={{ color: "#888" }}>Original sale</span>
                <strong>{fmt(showCollectForm.amount)}</strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}>
                <span style={{ color: "#888" }}>Already paid</span>
                <strong style={{ color: "#27500A" }}>{fmt(showCollectForm.paid)}</strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, fontWeight: 700 }}>
                <span>Balance due</span>
                <span style={{ color: "#A32D2D" }}>{fmt(showCollectForm.balance)}</span>
              </div>
            </div>

            <label style={S.label}>Amount received (₹)</label>
            <input style={S.input} type="number"
              placeholder={`Max: ₹${showCollectForm.balance}`}
              value={collectAmount}
              onChange={e => setCollectAmount(e.target.value)} />

            <label style={S.label}>Payment mode</label>
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              {["Cash", "UPI"].map(m => (
                <div key={m} onClick={() => setCollectMode(m)}
                  style={{
                    padding: "7px 20px", borderRadius: 8, fontSize: 13, cursor: "pointer",
                    background: collectMode === m ? (m === "Cash" ? "#EAF3DE" : "#E6F1FB") : "#F7F4EE",
                    color: collectMode === m ? (m === "Cash" ? "#27500A" : "#185FA5") : "#555",
                    border: "1px solid #E0DDD5",
                  }}>{m}</div>
              ))}
            </div>

            <label style={S.label}>Date</label>
            <input style={S.input} type="date" value={collectDate}
              onChange={e => setCollectDate(e.target.value)} />

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setShowCollectForm(null)}
                style={{ ...S.btn("#F7F4EE", "#555"), flex: 1, border: "1px solid #E0DDD5" }}>
                Cancel
              </button>
              <button onClick={handleCollect} disabled={saving}
                style={{ ...S.btn(), flex: 2 }}>
                {saving ? "Saving..." : "✓ Record Payment"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Summary */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
        <div style={{ background: "#FAECE7", borderRadius: 10, padding: "10px 12px", borderLeft: "3px solid #A32D2D" }}>
          <div style={{ fontSize: 11, color: "#993C1D", marginBottom: 3 }}>Outstanding</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#A32D2D" }}>{fmt(totalOutstanding)}</div>
        </div>
        <div style={{ background: "#EAF3DE", borderRadius: 10, padding: "10px 12px", borderLeft: "3px solid #27500A" }}>
          <div style={{ fontSize: 11, color: "#27500A", marginBottom: 3 }}>Collected</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#27500A" }}>{fmt(totalCollected)}</div>
        </div>
        <div style={{ background: "#F7F4EE", borderRadius: 10, padding: "10px 12px", borderLeft: "3px solid #888" }}>
          <div style={{ fontSize: 11, color: "#888", marginBottom: 3 }}>Total credit</div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>{fmt(totalCredit)}</div>
        </div>
      </div>

      {msg && <AlertBar text={msg} type="success" />}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        {[
          { id: "outstanding", label: `Outstanding (${outstanding.length})` },
          { id: "cleared",     label: `Cleared (${cleared.length})` },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{
              flex: 1, padding: "9px", borderRadius: 8, fontSize: 12, fontWeight: 600,
              cursor: "pointer", border: "1px solid #E0DDD5",
              background: tab === t.id ? "#27500A" : "#F7F4EE",
              color: tab === t.id ? "#C0DD97" : "#555",
              fontFamily: "'DM Sans', sans-serif",
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Outstanding dues */}
      {tab === "outstanding" && (
        outstanding.length === 0
          ? <EmptyState text="No outstanding dues! All credit sales are cleared." />
          : outstanding.map(s => (
            <div key={s.ts} style={S.card}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <OilDot oil={s.oil} />
                  <strong style={{ fontSize: 13 }}>{s.oil}</strong>
                  <span style={{ fontSize: 12, color: "#888", marginLeft: 4 }}>· {s.sku}</span>
                </div>
                <span style={{ fontSize: 12, color: "#888" }}>{s.date}</span>
              </div>

              {/* Buyer info */}
              {s.buyerName && (
                <div style={{
                  background: "#F7F4EE", borderRadius: 8, padding: "8px 10px",
                  marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center",
                }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>👤 {s.buyerName}</div>
                    {s.buyerPhone && (
                      <a href={`tel:${s.buyerPhone}`} style={{ fontSize: 11, color: "#185FA5", textDecoration: "none" }}>
                        📞 {s.buyerPhone}
                      </a>
                    )}
                  </div>
                  {s.buyerPhone && (
                    <a href={`https://wa.me/91${s.buyerPhone}`} target="_blank" rel="noreferrer"
                      style={{
                        background: "#25D366", color: "#fff", fontSize: 11,
                        padding: "4px 10px", borderRadius: 6, textDecoration: "none", fontWeight: 600,
                      }}>
                      WhatsApp
                    </a>
                  )}
                </div>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 10, fontSize: 12 }}>
                <div>
                  <div style={{ color: "#888" }}>Sale amount</div>
                  <strong>{fmt(s.amount)}</strong>
                </div>
                <div>
                  <div style={{ color: "#27500A" }}>Paid</div>
                  <strong style={{ color: "#27500A" }}>{fmt(s.paid)}</strong>
                </div>
                <div>
                  <div style={{ color: "#A32D2D" }}>Balance</div>
                  <strong style={{ color: "#A32D2D" }}>{fmt(s.balance)}</strong>
                </div>
              </div>

              <div style={{ fontSize: 11, color: "#aaa", marginBottom: 8 }}>
                {fmtL(s.litres)} · {s.location} · by {s.by}
              </div>

              {(user.admin || user.plant || user.shop) && (
                <button onClick={() => { setShowCollectForm(s); setCollectAmount(String(s.balance)); }}
                  style={{
                    width: "100%", padding: "8px", borderRadius: 8,
                    background: "#EAF3DE", color: "#27500A",
                    border: "1px solid #C0DD97", fontSize: 13,
                    fontWeight: 600, cursor: "pointer",
                    fontFamily: "'DM Sans', sans-serif",
                  }}>
                  💰 Record Payment — Mark as Received
                </button>
              )}
            </div>
          ))
      )}

      {/* Cleared sales */}
      {tab === "cleared" && (
        cleared.length === 0
          ? <EmptyState text="No cleared credit sales yet." />
          : cleared.map(s => (
            <div key={s.ts} style={{ ...S.card, opacity: 0.7 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <OilDot oil={s.oil} />
                  <strong style={{ fontSize: 13 }}>{s.oil}</strong>
                  <span style={{ fontSize: 10, background: "#EAF3DE", color: "#27500A", padding: "2px 8px", borderRadius: 8, marginLeft: 6 }}>
                    ✓ Cleared
                  </span>
                </div>
                <span style={{ fontSize: 12, fontWeight: 600 }}>{fmt(s.amount)}</span>
              </div>
              <div style={{ fontSize: 12, color: "#888" }}>
                {s.date} · {s.location} · by {s.by}
              </div>
            </div>
          ))
      )}
    </div>
  );
}
